<template>
  <div class="page">

    <!-- 搜索栏 -->
    <header class="search-bar">
      <div class="sb-input-wrap">
        <i class="fa-solid fa-magnifying-glass sb-icon"></i>
        <input
          ref="searchInput"
          v-model="keyword"
          type="text"
          placeholder="搜索商家、补贴政策或商品"
          @keyup.enter="doSearch"
        />
        <i v-if="keyword" class="fa-solid fa-xmark sb-clear" @click="keyword = ''"></i>
      </div>
      <span class="sb-cancel" @click="$router.back()">取消</span>
    </header>

    <!-- 搜索结果 -->
    <div v-if="searched" class="result-area">

      <!-- Tab 筛选 -->
      <div class="res-tabs">
        <div
          v-for="t in resTabs"
          :key="t.key"
          class="res-tab"
          :class="{ active: activeTab === t.key }"
          @click="activeTab = t.key"
        >
          {{ t.label }}
          <span v-if="getResultCount(t.key)">({{ getResultCount(t.key) }})</span>
        </div>
      </div>

      <!-- 商户结果 -->
      <div v-if="activeTab === 'shop'" class="res-list">
        <router-link
          v-for="m in shopResults"
          :key="m.id"
          :to="`/shop/${m.id}`"
          class="res-shop"
        >
          <div class="rs-img" :style="{ background: shopBg(m.category?.name) }">
            <img
              v-if="m.main_image"
              :src="imgUrl(m.main_image)"
              :alt="m.name"
              @error="e => e.target.style.display='none'"
            />
            <i v-else :class="shopIcon(m.category?.name)"></i>
          </div>
          <div class="rs-info">
            <h4>{{ m.name }}</h4>
            <p class="rs-loc">{{ m.town?.name }} · {{ m.address }}</p>
            <div class="rs-tags">
              <span v-if="m.category" class="rs-tag">{{ m.category.name }}</span>
              <span v-if="m.rating" class="rs-rating"><i class="fa-solid fa-star"></i> {{ m.rating }}</span>
            </div>
          </div>
        </router-link>
        <div v-if="!shopResults.length" class="res-empty">未找到相关商户</div>
      </div>

      <!-- 商品结果 -->
      <div v-if="activeTab === 'product'" class="res-list">
        <router-link
          v-for="p in productResults"
          :key="p.id"
          :to="`/product/${p.id}`"
          class="res-product"
        >
          <div class="rp-img">
            <img :src="p.image ? imgUrl(p.image) : '/no_pic.png'" :alt="p.name" />
            <span v-if="p.tag" class="rp-tag">{{ p.tag }}</span>
          </div>
          <div class="rp-info">
            <h4>{{ p.name }}</h4>
            <p v-if="p.merchant?.name" class="rp-shop">{{ p.merchant.name }}</p>
            <p class="rp-price">¥{{ p.subsidy_price || p.original_price }}</p>
            <p v-if="p.original_price && p.subsidy_price" class="rp-orig"><s>¥{{ p.original_price }}</s></p>
          </div>
        </router-link>
        <div v-if="!productResults.length" class="res-empty">未找到相关商品</div>
      </div>

      <!-- 政策结果 -->
      <div v-if="activeTab === 'policy'" class="res-list">
        <div
          v-for="p in policyResults"
          :key="p.id"
          class="res-policy"
        >
          <div class="rpy-icon">{{ p.icon || '📋' }}</div>
          <div class="rpy-info">
            <h4>{{ p.title }}</h4>
            <p class="rpy-desc">{{ p.description || '政府补贴·线上流程·足不出户' }}</p>
            <p class="rpy-price">最高补贴 <strong>{{ p.max_subsidy }}元</strong></p>
          </div>
        </div>
        <div v-if="!policyResults.length" class="res-empty">未找到相关政策</div>
      </div>

      <!-- 加载中 -->
      <div v-if="loading" class="loading">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>搜索中...</span>
      </div>
    </div>

    <!-- 搜索建议 / 历史 -->
    <div v-else class="suggest-area">

      <!-- 搜索历史 -->
      <div v-if="history.length" class="section">
        <div class="sec-hd">
          <h3>搜索历史</h3>
          <i class="fa-solid fa-trash-can" @click="clearHistory"></i>
        </div>
        <div class="tag-list">
          <span v-for="(h, i) in history" :key="i" class="tag" @click="keyword = h; doSearch()">{{ h }}</span>
        </div>
      </div>

      <!-- 热门搜索 -->
      <div class="section">
        <div class="sec-hd">
          <h3>热门搜索</h3>
        </div>
        <div class="tag-list">
          <span v-for="(h, i) in hotWords" :key="i" class="tag hot" @click="keyword = h; doSearch()">{{ h }}</span>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
export default {
  name: 'SearchPage',

  data() {
    return {
      keyword: '',
      searched: false,
      loading: false,
      activeTab: 'shop',

      shopResults: [],
      productResults: [],
      policyResults: [],

      history: JSON.parse(localStorage.getItem('search_history') || '[]'),
      hotWords: ['以旧换新', '家电补贴', '餐饮美食', '商超便利', '电动车', '手机'],

      resTabs: [
        { key: 'shop', label: '商户' },
        { key: 'product', label: '商品' },
        { key: 'policy', label: '政策' }
      ],

      shopFaIcons: {
        '餐饮美食': 'fa-solid fa-shrimp',
        '商超便利': 'fa-solid fa-store',
        '电器数码': 'fa-solid fa-tv',
        '家居建材': 'fa-solid fa-couch',
        '汽车服务': 'fa-solid fa-car',
        '美容美发': 'fa-solid fa-scissors',
        '休闲娱乐': 'fa-solid fa-gamepad',
        '医疗健康': 'fa-solid fa-heart-pulse'
      },

      shopColors: {
        '餐饮美食': '#fff7ed',
        '商超便利': '#eff6ff',
        '电器数码': '#eff6ff',
        '家居建材': '#fdf4ff',
        '汽车服务': '#f0fdf4',
        '美容美发': '#fdf2f8',
        '休闲娱乐': '#fefce8',
        '医疗健康': '#fef2f2'
      }
    }
  },

  mounted() {
    this.$refs.searchInput?.focus()
  },

  methods: {
    async doSearch() {
      const kw = this.keyword.trim()
      if (!kw) return

      this.searched = true
      this.loading = true
      this.saveHistory(kw)

      try {
        // 并行搜索商户、商品、政策
        const [shopRes, productRes, policyRes] = await Promise.all([
          this.$api.get(`/api/v1/merchants?keyword=${encodeURIComponent(kw)}&pageSize=20`),
          this.$api.get(`/api/v1/products/search?keyword=${encodeURIComponent(kw)}&pageSize=20`),
          this.$api.get(`/api/v1/policies?keyword=${encodeURIComponent(kw)}&pageSize=20`)
        ])

        this.shopResults = shopRes.data.data?.list || []
        this.productResults = productRes.data.data?.list || []

        // 政策结果
        this.policyResults = policyRes.data.data?.list || []

        // 默认 Tab
        if (this.shopResults.length) this.activeTab = 'shop'
        else if (this.productResults.length) this.activeTab = 'product'
        else if (this.policyResults.length) this.activeTab = 'policy'
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    getResultCount(key) {
      if (key === 'shop') return this.shopResults.length
      if (key === 'product') return this.productResults.length
      if (key === 'policy') return this.policyResults.length
      return 0
    },

    saveHistory(kw) {
      const arr = this.history.filter(h => h !== kw)
      arr.unshift(kw)
      this.history = arr.slice(0, 10)
      localStorage.setItem('search_history', JSON.stringify(this.history))
    },

    clearHistory() {
      this.history = []
      localStorage.removeItem('search_history')
    },

    imgUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      const base = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000'
      return base + (path.startsWith('/') ? path : '/' + path)
    },

    shopIcon(cat) {
      return this.shopFaIcons[cat] || 'fa-solid fa-store'
    },

    shopBg(cat) {
      return this.shopColors[cat] || '#f3f4f6'
    },

    goodsIcon(name) {
      if (/电扇|风扇/.test(name)) return 'fa-solid fa-fan'
      if (/月饼/.test(name)) return 'fa-solid fa-gift'
      if (/饭煲|电饭/.test(name)) return 'fa-solid fa-kitchen-set'
      if (/炸锅/.test(name)) return 'fa-solid fa-bowl-rice'
      if (/手机/.test(name)) return 'fa-solid fa-mobile-screen'
      if (/电视/.test(name)) return 'fa-solid fa-tv'
      return 'fa-solid fa-box-open'
    }
  }
}
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

.page {
  width: 100%;
  min-height: 100vh;
  background: #f4f8fb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #1f2937;
}

/* ================================================================
   搜索栏
   ================================================================ */
.search-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: linear-gradient(180deg, #3b82f6, #60a5fa);
}

.sb-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-radius: 20px;
  padding: 0 10px;
  height: 36px;
}

.sb-icon { color: #9ca3af; font-size: 13px; }

.sb-input-wrap input {
  flex: 1;
  border: none;
  outline: none;
  background: none;
  font-size: 14px;
  color: #1f2937;
  min-width: 0;
}

.sb-input-wrap input::placeholder { color: #9ca3af; }

.sb-clear {
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
}

.sb-cancel {
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
}

/* ================================================================
   结果 Tab
   ================================================================ */
.res-tabs {
  display: flex;
  gap: 0;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
}

.res-tab {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all .2s;
}

.res-tab.active {
  color: #2563eb;
  font-weight: 700;
  border-bottom-color: #2563eb;
}

.res-tab span { font-size: 11px; }

/* ================================================================
   商户结果
   ================================================================ */
.res-list {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.res-shop {
  display: flex;
  gap: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 10px;
  text-decoration: none;
  color: inherit;
}

.res-shop:active { background: #f9fafb; }

.rs-img {
  width: 70px;
  height: 70px;
  min-width: 70px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.rs-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rs-img i { font-size: 24px; color: #d1d5db; }

.rs-info {
  flex: 1;
  min-width: 0;
}

.rs-info h4 {
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rs-loc {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.rs-tags {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  align-items: center;
}

.rs-tag {
  font-size: 10px;
  padding: 2px 6px;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 4px;
}

.rs-rating {
  font-size: 11px;
  color: #f59e0b;
  font-weight: 600;
}

.rs-rating i { font-size: 9px; }

/* ================================================================
   商品结果
   ================================================================ */
.res-product {
  display: flex;
  gap: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 10px;
  text-decoration: none;
  color: inherit;
}

.res-product:active { background: #f9fafb; }

.rp-img {
  width: 70px;
  height: 70px;
  min-width: 70px;
  border-radius: 10px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.rp-img img {
  width: 100%;
  height: auto;
}
.rp-img i { font-size: 24px; color: #d1d5db; }

.rp-tag {
  position: absolute;
  top: 0;
  left: 0;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  padding: 2px 5px;
  border-radius: 8px 0 8px 0;
}

.rp-info {
  flex: 1;
  min-width: 0;
}

.rp-info h4 {
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rp-price {
  font-size: 16px;
  font-weight: 800;
  color: #ef4444;
  margin-top: 4px;
}

.rp-orig {
  font-size: 11px;
  color: #9ca3af;
}

.rp-shop {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ================================================================
   政策结果
   ================================================================ */
.res-policy {
  display: flex;
  gap: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 12px;
}

.rpy-icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 10px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.rpy-info { flex: 1; min-width: 0; }

.rpy-info h4 {
  font-size: 14px;
  font-weight: 700;
}

.rpy-desc {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.rpy-price {
  font-size: 12px;
  color: #f97316;
  margin-top: 4px;
}

.rpy-price strong {
  font-size: 16px;
  font-weight: 800;
}

.res-empty {
  text-align: center;
  padding: 40px 0;
  color: #9ca3af;
  font-size: 14px;
}

/* ================================================================
   搜索建议 / 历史
   ================================================================ */
.suggest-area {
  padding: 16px 12px;
}

.section {
  margin-bottom: 24px;
}

.sec-hd {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.sec-hd h3 {
  font-size: 15px;
  font-weight: 700;
}

.sec-hd i {
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  padding: 6px 14px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e5e7eb;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  transition: all .2s;
}

.tag:active { background: #f3f4f6; }

.tag.hot {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #c2410c;
}

/* ================================================================
   加载
   ================================================================ */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  color: #9ca3af;
}

.loading i {
  font-size: 24px;
  margin-bottom: 8px;
}

/* ================================================================
   响应式
   ================================================================ */
@media (min-width: 481px) {
  .res-list { padding: 0 16px 20px; }
  .rs-img { width: 80px; height: 80px; min-width: 80px; }
  .rp-img { width: 80px; height: 80px; min-width: 80px; }
}
</style>
